<template>
  <footer class="Footer">
    <div class="Footer_Top">
      <section class="Footer_Contact">
        <h2 class="Footer_Title">{{ translations.contact.title }}</h2>
        <div class="Footer_ContactInfo">
          <p class="Footer_Text">
            {{ translations.contact.tel }}<br />
            <a class="Footer_Link" :href="`mailto:${translations.contact.email}`">{{ translations.contact.email }}</a>
          </p>
          <p class="Footer_Text">{{ translations.contact.opening }}</p>
        </div>
        <ul class="Footer_Social">
          <li v-for="link in socialLinks" :key="link.label">
            <a class="Footer_SocialLink" :href="link.url" target="_blank" rel="noopener" :aria-label="link.label">
              <img :src="link.icon" alt="" width="16" height="16" />
            </a>
          </li>
        </ul>
      </section>

      <!-- Desktop only: on mobile the hamburger stays visible, so the mockup drops the site map -->
      <nav class="Footer_SiteMap" :aria-label="translations.siteMap.title">
        <h2 class="Footer_Title">{{ translations.siteMap.title }}</h2>
        <ul class="Footer_SiteMapList">
          <li><router-link class="Footer_Link" to="/">{{ navigation.home }}</router-link></li>
          <li><router-link class="Footer_Link" to="/a-propos">{{ navigation.about }}</router-link></li>
          <li><router-link class="Footer_Link" to="/usages">{{ navigation.usages }}</router-link></li>
          <li>
            <router-link class="Footer_Link" to="/offres-de-spectacles">
              {{ translations.siteMap.showsDirectory }}
            </router-link>
          </li>
          <li><a class="Footer_Link" :href="connectionUrl" target="_blank">{{ navigation.connexion }}</a></li>
        </ul>
      </nav>

      <section class="Footer_Newsletter">
        <h2 class="Footer_Title">{{ translations.newsletter.title }}</h2>
        <p class="Footer_Text">{{ translations.newsletter.text }}</p>
        <a class="Footer_Btn" :href="translations.newsletter.url" target="_blank" rel="noopener">
          {{ translations.newsletter.btnText }}
          <img class="Footer_BtnChevron" :src="chevronRightUrl" alt="" width="16" height="16" />
        </a>
      </section>
    </div>

    <div class="Footer_Bottom">
      <ul class="Footer_Legal">
        <li v-for="link in legalLinks" :key="link.label">
          <!-- Pages of the vitrine (path) open in the same tab, external documents in a new one -->
          <router-link v-if="link.url.startsWith('/')" class="Footer_Link" :to="link.url">
            {{ link.label }}
          </router-link>
          <a v-else class="Footer_Link" :href="link.url" target="_blank" rel="noopener">{{ link.label }}</a>
        </li>
        <li>
          <button class="Footer_Link Footer_CookiesBtn" type="button" @click="manageCookies">
            {{ translations.legal.manageCookies }}
          </button>
        </li>
      </ul>
      <picture class="Footer_Logo">
        <source :srcset="logoHorizontalUrl" media="(min-width: 992px)" width="1047" height="151" />
        <img :src="logoStackedUrl" :alt="translations.logoAlt" width="289" height="157" />
      </picture>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import translationsData from '@/dictionaries/footer.json';
import navigationData from '@/dictionaries/navigation.json';
import facebookIconUrl from '@/assets/img/icon-facebook-blanc.svg';
import linkedinIconUrl from '@/assets/img/icon-linkedin-blanc.svg';
import youtubeIconUrl from '@/assets/img/icon-youtube-blanc.svg';
import logoHorizontalUrl from '@/assets/img/logo-scene-pro-horizontal-blanc.svg';
import logoStackedUrl from '@/assets/img/logo-scene-pro-grand-blanc.svg';
import chevronRightUrl from '@/assets/img/chevron-droite-blanc.svg';
import { deleteCookie } from '@/services/handleCookies';
import { useCookieConsent } from '@/services/cookieConsent';

const translations = translationsData.fr;
const navigation = navigationData.fr;
const connectionUrl = process.env.VUE_APP_LOGIN_URL;

const { social } = translations.contact;
const { terms, privacy } = translations.legal;

// Links without a URL in the dictionary are not rendered, so no dead "#" link ships
const socialLinks = [
  { ...social.facebook, icon: facebookIconUrl },
  { ...social.linkedin, icon: linkedinIconUrl },
  { ...social.youtube, icon: youtubeIconUrl },
].filter((link) => link.url);
const legalLinks = [terms, privacy].filter((link) => link.url);

const { COOKIE_CONSENT_KEY } = useCookieConsent();

// Deleting the consent cookie makes the cookie banner show up again
const manageCookies = () => {
  deleteCookie(COOKIE_CONSENT_KEY);
  window.dispatchEvent(new Event('cookie-consent-changed'));
};
</script>

<style lang="scss" scoped src="./TheFooterComponent.scss"></style>
